import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, Send, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Markdown } from "@/lib/markdown";
import { setUiFlag } from "@/lib/uiState";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEARS = ["2026", "2027", "2028"];
const DURATIONS = ["About 3–5 nights", "About 7–10 nights", "About 10–14 nights", "2+ weeks"];
const SEASONS = ["Spring", "Summer", "Fall", "Winter", "No preference — suggest the best timing"];
const UNSURE_YEARS = ["2026", "2027", "2028", "Flexible on the year"];
const UNSURE_DURATIONS = ["A long weekend", "About one week", "About two weeks", "2+ weeks"];

const STEPS = [
  { key: "who", q: "Who are you traveling with?", type: "single", options: ["Just the two of us", "Family", "Friends", "A larger group", "Solo", "Colleagues / work"] },
  { key: "type", q: "What type of trip are you considering?", type: "single", options: ["Cruise", "All-inclusive resort", "Villa", "Honeymoon", "Celebration or wedding", "Group trip", "Not sure yet"] },
  { key: "when", q: "When would you like to travel?", type: "dates" },
  { key: "party", q: "How many people are traveling, and how many rooms will you need?", type: "party" },
  { key: "vibe", q: "What matters most on this trip?", type: "multi", options: ["Relaxation", "Food", "Culture", "Nightlife", "Adventure", "Family time", "Romance", "Celebration"] },
  { key: "where", q: "Any destinations already on your mind?", type: "text", placeholder: "Caribbean, Europe, Thailand, Italy, somewhere warm, or help me decide" },
];

const btn = "font-body text-xs uppercase tracking-[0.2em] transition-colors duration-300";
const field = "w-full bg-transparent border-b border-white/15 py-3 font-body text-ink placeholder-white/30 focus:border-gold focus:outline-none transition-colors [color-scheme:dark]";
const selCls = `${field} appearance-none cursor-pointer`;
const lbl = "block font-body text-[11px] uppercase tracking-[0.15em] text-white/60 mb-1";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// Compose the readable timing string stored on the quote (admin + email friendly).
export function describeDates(a) {
  if (!a) return "";
  if (a.mode === "exact") return `${fmtDate(a.departure)} → ${fmtDate(a.return)} (exact dates)`;
  if (a.mode === "approximate") return `${a.window} ${a.year} · ${a.duration} (approximate dates)`;
  if (a.mode === "unsure") return `Not fixed yet: ${a.window} · ${a.year} · ${a.duration}`;
  return "";
}

function Stepper({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <p className={lbl}>{label}</p>
      <div className="flex items-center gap-5 border-b border-white/15 py-2">
        <button type="button" aria-label={`Decrease ${label.toLowerCase()}`} data-testid={`${testid}-minus`}
          onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
          className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/70 hover:border-gold hover:text-gold disabled:opacity-30 transition-colors">
          <Minus size={15} />
        </button>
        <span data-testid={`${testid}-value`} aria-live="polite" className="font-serif text-3xl font-light w-10 text-center">{value}</span>
        <button type="button" aria-label={`Increase ${label.toLowerCase()}`} data-testid={`${testid}-plus`}
          onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}
          className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/70 hover:border-gold hover:text-gold disabled:opacity-30 transition-colors">
          <Plus size={15} />
        </button>
      </div>
    </div>
  );
}

function DatePathway({ value, onChange }) {
  const v = value || { mode: "" };
  const set = (patch) => onChange({ ...v, ...patch });
  const modeBtn = (mode, label, testid) => (
    <button key={mode} type="button" data-testid={testid} onClick={() => onChange({ mode })}
      className={`px-5 py-3 border font-body text-sm transition-colors ${v.mode === mode ? "bg-gold text-black border-gold" : "border-white/15 text-white/75 hover:border-gold hover:text-gold"}`}>
      {label}
    </button>
  );
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-8" role="group" aria-label="How certain are your travel dates?">
        {modeBtn("exact", "I know my exact dates", "pp-dates-exact")}
        {modeBtn("approximate", "My dates are approximate", "pp-dates-approx")}
        {modeBtn("unsure", "I'm not sure yet", "pp-dates-unsure")}
      </div>
      {v.mode === "exact" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div><label htmlFor="pp-dep" className={lbl}>Departure Date</label>
            <input id="pp-dep" data-testid="pp-departure" type="date" min={todayISO()} className={field}
              value={v.departure || ""} onChange={(e) => set({ departure: e.target.value, return: v.return && v.return < e.target.value ? "" : v.return })} /></div>
          <div><label htmlFor="pp-ret" className={lbl}>Return Date</label>
            <input id="pp-ret" data-testid="pp-return" type="date" min={v.departure || todayISO()} className={field}
              value={v.return || ""} onChange={(e) => set({ return: e.target.value })} /></div>
        </div>
      )}
      {v.mode === "approximate" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div><label htmlFor="pp-window" className={lbl}>Preferred month or window</label>
            <select id="pp-window" data-testid="pp-window" className={selCls} value={v.window || ""} onChange={(e) => set({ window: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {MONTHS.map((m) => <option key={m} value={m} className="bg-noir">{m}</option>)}
            </select></div>
          <div><label htmlFor="pp-year" className={lbl}>Year</label>
            <select id="pp-year" data-testid="pp-year" className={selCls} value={v.year || ""} onChange={(e) => set({ year: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {YEARS.map((y) => <option key={y} value={y} className="bg-noir">{y}</option>)}
            </select></div>
          <div><label htmlFor="pp-duration" className={lbl}>Approximate trip duration</label>
            <select id="pp-duration" data-testid="pp-duration" className={selCls} value={v.duration || ""} onChange={(e) => set({ duration: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {DURATIONS.map((d) => <option key={d} value={d} className="bg-noir">{d}</option>)}
            </select></div>
        </div>
      )}
      {v.mode === "unsure" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div><label htmlFor="pp-uwindow" className={lbl}>Rough season or window</label>
            <select id="pp-uwindow" data-testid="pp-uwindow" className={selCls} value={v.window || ""} onChange={(e) => set({ window: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {SEASONS.map((s) => <option key={s} value={s} className="bg-noir">{s}</option>)}
            </select></div>
          <div><label htmlFor="pp-uyear" className={lbl}>Approximate year</label>
            <select id="pp-uyear" data-testid="pp-uyear" className={selCls} value={v.year || ""} onChange={(e) => set({ year: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {UNSURE_YEARS.map((y) => <option key={y} value={y} className="bg-noir">{y}</option>)}
            </select></div>
          <div><label htmlFor="pp-uduration" className={lbl}>Approximate trip length</label>
            <select id="pp-uduration" data-testid="pp-uduration" className={selCls} value={v.duration || ""} onChange={(e) => set({ duration: e.target.value })}>
              <option value="" className="bg-noir">Select…</option>
              {UNSURE_DURATIONS.map((d) => <option key={d} value={d} className="bg-noir">{d}</option>)}
            </select></div>
        </div>
      )}
    </div>
  );
}

export default function PassportPlanner({ onSend }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");

  const reset = () => { setStep(0); setAnswers({}); setSummary(""); setLoading(false); };
  const close = () => { setOpen(false); setTimeout(reset, 300); };

  // Let sitewide UI (sticky CTA / Passport List popup) yield while the planner is open.
  useEffect(() => { setUiFlag("overlay", open); return () => setUiFlag("overlay", false); }, [open]);

  const cur = STEPS[step];
  const val = answers[cur?.key];

  const setSingle = (o) => setAnswers({ ...answers, [cur.key]: o });
  const toggleMulti = (o) => {
    const arr = Array.isArray(val) ? val : [];
    setAnswers({ ...answers, [cur.key]: arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o] });
  };

  const validateStep = () => {
    if (cur.type === "dates") {
      const w = val || {};
      if (!w.mode) { toast.error("Choose the option that fits your dates best."); return false; }
      if (w.mode === "exact") {
        if (!w.departure || !w.return) { toast.error("Please select both your departure and return dates."); return false; }
        if (w.return < w.departure) { toast.error("Your return date can't be before your departure date."); return false; }
        return true;
      }
      if (!w.window || !w.year || !w.duration) { toast.error("Please choose a rough window, year and trip length so we can plan intelligently."); return false; }
      if (w.mode === "unsure" && w.window.startsWith("No preference") && w.year.startsWith("Flexible")) {
        toast.error("Give us at least a rough season or year to work with."); return false;
      }
      return true;
    }
    if (cur.type === "party") {
      const p = val || {};
      if (!p.travelers || !p.rooms) { toast.error("Please set both travelers and rooms."); return false; }
      if (p.rooms > p.travelers) { toast.error("Rooms can't exceed the number of travelers."); return false; }
      return true;
    }
    return true;
  };

  const next = async () => {
    if (!validateStep()) return;
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setLoading(true);
    try {
      const readable = STEPS.map((s) => {
        const a = answers[s.key];
        if (s.type === "dates") return [s.q, describeDates(a)];
        if (s.type === "party") return [s.q, a ? `${a.travelers} traveler${a.travelers > 1 ? "s" : ""} · ${a.rooms} room${a.rooms > 1 ? "s" : ""}` : ""];
        return [s.q, Array.isArray(a) ? a.join(", ") : (a || "")];
      });
      const payload = { answers: Object.fromEntries(readable) };
      const { data } = await api.post("/passport-planner", payload);
      setSummary(data.summary);
    } catch {
      setSummary("We couldn't generate ideas just now, but our specialists would love to help — send your notes below and we'll take it from here.");
    } finally { setLoading(false); }
  };

  const send = () => {
    const readable = STEPS.map((s) => {
      const a = answers[s.key];
      const v = s.type === "dates" ? describeDates(a) : s.type === "party" ? (a ? `${a.travelers} travelers · ${a.rooms} rooms` : "—") : (Array.isArray(a) ? a.join(", ") : (a || "—"));
      return `${s.q} ${v}`;
    }).join("\n");
    const details = `— From Passport Planner —\n${readable}\n\nTrip ideas:\n${summary}`;
    const w = answers.when || {};
    const p = answers.party || {};
    onSend?.({
      details,
      travel_type: mapType(answers.type),
      destination: answers.where || "",
      travelers: p.travelers ? String(p.travelers) : "",
      rooms: p.rooms ? String(p.rooms) : "",
      travel_dates: describeDates(w),
      date_type: w.mode === "exact" ? "exact" : "approximate",
      departure_date: w.mode === "exact" ? (w.departure || "") : "",
      return_date: w.mode === "exact" ? (w.return || "") : "",
      travel_window: w.mode === "exact" ? "" : (w.window || ""),
      travel_year: w.mode === "exact" ? "" : (w.year || ""),
      trip_duration: w.mode === "exact" ? "" : (w.duration || ""),
    });
    close();
  };

  return (
    <>
      <button data-testid="open-passport-planner" onClick={() => setOpen(true)}
        className="group w-full text-left border border-gold/40 bg-gradient-to-br from-[#12100a] to-noir p-8 hover:border-gold transition-colors duration-300">
        <div className="flex items-center gap-3 mb-4"><Sparkles size={20} className="text-gold" /><span className="font-body text-[11px] uppercase tracking-[0.3em] text-gold">Passport Planner</span></div>
        <h3 className="font-serif text-2xl md:text-3xl font-light">Not sure where to start? <span className="italic text-white/70">Get trip ideas in a minute.</span></h3>
        <p className="font-body text-white/70 text-sm mt-3 font-light">Answer a few quick questions and get personalized trip ideas in about a minute — then send them straight to our team.</p>
        <span className="mt-5 inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em]">Find My Trip <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" /></span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-6" onClick={close}>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              role="dialog" aria-modal="true" aria-label="Passport Planner"
              className="bg-[#0a0a0a] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
                <div className="flex items-center gap-2"><Sparkles size={16} className="text-gold" /><span className="font-body text-[11px] uppercase tracking-[0.3em] text-gold">Passport Planner</span></div>
                <button data-testid="close-passport-planner" aria-label="Close Passport Planner" onClick={close} className="text-white/40 hover:text-gold"><X size={22} /></button>
              </div>

              <div className="p-6 md:p-10">
                {!summary && !loading && (
                  <>
                    <p className="font-body text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">Step {step + 1} of {STEPS.length}</p>
                    <div className="w-full h-px bg-white/10 mb-8"><div className="h-px bg-gold transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
                    <h3 className="font-serif text-2xl md:text-3xl font-light mb-8">{cur.q}</h3>

                    {cur.type === "text" && (
                      <input data-testid="pp-text" autoFocus value={val || ""} onChange={(e) => setSingle(e.target.value)} placeholder={cur.placeholder}
                        className="w-full bg-transparent border-b border-white/15 py-3 text-ink placeholder-white/30 focus:border-gold focus:outline-none transition-colors text-lg" />
                    )}
                    {cur.type === "dates" && <DatePathway value={val} onChange={setSingle} />}
                    {cur.type === "party" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <Stepper label="Travelers" testid="pp-travelers" min={1} max={30} value={val?.travelers || 0}
                          onChange={(n) => setSingle({ rooms: Math.min(val?.rooms || 0, n) || 0, travelers: n })} />
                        <Stepper label="Rooms" testid="pp-rooms" min={1} max={15} value={val?.rooms || 0}
                          onChange={(n) => setSingle({ ...(val || {}), rooms: val?.travelers ? Math.min(n, val.travelers) : n })} />
                      </div>
                    )}
                    {(cur.type === "single" || cur.type === "multi") && (
                      <div className="flex flex-wrap gap-3">
                        {cur.options.map((o) => {
                          const active = cur.type === "multi" ? (Array.isArray(val) && val.includes(o)) : val === o;
                          return (
                            <button key={o} data-testid={`pp-option-${o}`} onClick={() => cur.type === "multi" ? toggleMulti(o) : setSingle(o)}
                              className={`px-5 py-3 border font-body text-sm transition-colors ${active ? "bg-gold text-black border-gold" : "border-white/15 text-white/75 hover:border-gold hover:text-gold"}`}>{o}</button>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-10">
                      <button data-testid="pp-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                        className={`${btn} flex items-center gap-2 text-white/50 hover:text-gold disabled:opacity-30`}><ArrowLeft size={14} /> Back</button>
                      <button data-testid="pp-next" onClick={next}
                        className={`${btn} flex items-center gap-2 border border-gold text-gold px-7 py-3.5 hover:bg-gold hover:text-black`}>
                        {step === STEPS.length - 1 ? "Get My Trip Ideas" : "Next"} <ArrowRight size={14} />
                      </button>
                    </div>
                  </>
                )}

                {loading && (
                  <div className="py-16 text-center">
                    <Sparkles size={28} className="text-gold mx-auto mb-5 animate-pulse" />
                    <p className="font-serif text-2xl font-light">Dreaming up a few ideas…</p>
                  </div>
                )}

                {summary && !loading && (
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Your Trip Ideas</p>
                    <div data-testid="pp-summary" className="font-body text-white/80 text-base leading-relaxed font-light border-l-2 border-gold/40 pl-5">
                      <Markdown text={summary} />
                    </div>
                    <p className="font-body text-white/35 text-xs mt-6 font-light">Passport Planner uses artificial intelligence. These are ideas for inspiration — not confirmed recommendations, bookings, or prices, and availability isn't guaranteed. Information you enter may be processed by technology/AI service providers to run this feature. Please don't enter financial, login, government ID, or passport details. An Inforno Travel Experience Specialist will help finalize the details. See our <Link to="/privacy" className="text-gold link-underline">Privacy Policy</Link>.</p>
                    <div className="flex flex-col sm:flex-row gap-4 mt-8">
                      <button data-testid="pp-send" onClick={send} className={`${btn} flex items-center justify-center gap-2 border border-gold bg-gold text-black px-7 py-4 hover:bg-transparent hover:text-gold`}>
                        <Send size={14} /> Send My Trip Ideas to Inforno
                      </button>
                      <button onClick={reset} className={`${btn} text-white/50 hover:text-gold`}>Start over</button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function mapType(t) {
  const m = { "Cruise": "Cruises", "All-inclusive resort": "All-Inclusive Resorts", "Villa": "Villas & Private Escapes", "Honeymoon": "Romance Travel & Honeymoons", "Celebration or wedding": "Destination Celebrations", "Group trip": "Luxury Group Travel" };
  return m[t] || "Custom Travel Experiences";
}
